import { Capacitor } from '@capacitor/core'

/**
 * Eine fertige Datei dem Vermieter in die Hand geben.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM DAS NICHT „EIN DOWNLOAD" IST
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Im Browser heißt „sichern" `<a download>`, und das ist richtig. In der
 * iOS-Hülle heißt es nichts: eine WebView hat keine Downloadverwaltung, die der
 * Vermieter öffnen könnte. Und eine Datei, die über `@capacitor/filesystem` in
 * den Sandkasten geschrieben wird, ist in der App „Dateien" UNSICHTBAR —
 * nachgemessen: `ios/App/App/Info.plist` trägt weder `UIFileSharingEnabled`
 * noch `LSSupportsOpeningDocumentsInPlace`.
 *
 * Ohne diesen Unterschied wäre der PDF-Knopf in der Hülle genau so tot wie
 * `window.print()` heute — er würde eine Datei erzeugen, die niemand anfassen
 * kann. Deshalb geht es dort über das Teilen-Blatt des Betriebssystems: der
 * Vermieter wählt Mail, „Dateien", AirDrop oder was sonst installiert ist, und
 * hat die Datei in einem Griff dort, wo er sie braucht.
 *
 * DER GEGENVORSCHLAG, UND WARUM ER VERWORFEN IST: die beiden Plist-Schlüssel zu
 * setzen legt den gesamten Dokumentenordner der App dauerhaft für jede Person
 * offen, die das Gerät in der Hand hält — für eine Datei, die der Vermieter
 * danach in „Dateien" noch suchen müsste. Drei Handgriffe wären wieder drei.
 * Das Teilen-Blatt kostet ein Plugin und ist der Weg, den Vorgang 008 ohnehin
 * geht.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WAS HIER NOCH NICHT STEHT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Kein Begleittext, kein Empfänger, kein `mailto:`-Rückfallweg, kein Vermerk.
 * Das ist Schnitt 4 des Vorgangs (`lib/uebergabe.ts`) und ausdrücklich nicht
 * freigegeben. Diese Funktion tut eines: sie reicht eine Datei hinaus.
 */
export type Sicherweg = 'geteilt' | 'heruntergeladen' | 'abgebrochen'

export interface Datei {
  name: string
  bytes: Uint8Array
  mime?: string
  /** Optional context binding, checked again immediately before external handover. */
  stillCurrent?: () => boolean
}

/** Läuft das Sichern über das Teilen-Blatt? Entscheidet, welchen Satz die Oberfläche zeigt. */
export function sichernTeilt(): boolean {
  return Capacitor.isNativePlatform()
}

export async function dateiSichern(datei: Datei): Promise<Sicherweg> {
  if (datei.stillCurrent && !datei.stillCurrent()) return 'abgebrochen'
  if (!Capacitor.isNativePlatform()) {
    herunterladen(datei)
    return 'heruntergeladen'
  }

  const { Filesystem, Directory } = await import('@capacitor/filesystem')
  const { Share } = await import('@capacitor/share')
  if (datei.stillCurrent && !datei.stillCurrent()) return 'abgebrochen'

  /*
   * Zwischenspeicher, nicht Dokumentenordner: die Datei ist ein Durchgangsposten.
   * Sie enthält Namen, Anschrift und Beträge eines Gastes — was davon dauerhaft
   * aufbewahrt wird, entscheidet das Ziel, das der Vermieter im Teilen-Blatt
   * wählt, nicht diese Funktion.
   */
  const geschrieben = await Filesystem.writeFile({
    path: datei.name,
    directory: Directory.Cache,
    data: alsBase64(datei.bytes),
  })

  try {
    if (datei.stillCurrent && !datei.stillCurrent()) {
      return 'abgebrochen'
    }
    await Share.share({ title: datei.name, files: [geschrieben.uri] })
    return 'geteilt'
  } catch (fehler) {
    // Der Abbruch des Teilen-Blattes ist keine Störung, sondern eine Antwort.
    if (istAbbruch(fehler)) return 'abgebrochen'
    throw fehler
  } finally {
    // Share resolves when the native completion handler has finished. Remove
    // this staging copy after success as well as cancellation/context changes.
    // A copy deliberately saved by the user at a chosen destination is separate.
    await Filesystem.deleteFile({ path: datei.name, directory: Directory.Cache })
      .catch(() => { console.warn('STAYSUITE_SHARE_CACHE_CLEANUP_FAILED') })
  }
}

function istAbbruch(fehler: unknown): boolean {
  const text = fehler instanceof Error ? fehler.message : String(fehler)
  return /abort|cancel/i.test(text)
}

function herunterladen(datei: Datei): void {
  // `as BlobPart`: die Bytes stammen aus pdf-lib und sind ein gewöhnliches
  // Uint8Array; TypeScript kennt seit ES2024 den generischen ArrayBufferLike
  // und schaut deshalb schief.
  const blob = new Blob([datei.bytes as BlobPart], { type: datei.mime ?? 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = datei.name
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Erst freigeben, wenn der Browser den Klick verarbeitet hat. Sofortiges
  // `revoke` bricht den Download in Safari ab, ohne eine Meldung.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/** Bytes → Base64, in Stücken. `btoa(String.fromCharCode(...alles))` sprengt den Stapel. */
function alsBase64(bytes: Uint8Array): string {
  let roh = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    roh += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(roh)
}
